package redmcrosco.alex.farmatch.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import farmatch.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import redmcrosco.alex.farmatch.models.*
import kotlinx.datetime.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProfileDetailScreen(
    farmacista: Farmacista? = null,
    farmacia: Farmacia? = null,
    viewModel: AuthViewModel? = null,
    onBack: () -> Unit
) {
    val targetId = farmacista?.id ?: farmacia?.id ?: ""
    val targetNome = farmacista?.let { "${it.nome} ${it.cognome}" } ?: farmacia?.nomeFarmacia ?: ""
    val targetEmail = farmacista?.email ?: farmacia?.email ?: ""
    val targetRuolo = if (farmacista != null) "FARMACISTA" else "FARMACIA"

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(targetNome.ifEmpty { stringResource(Res.string.profile_detail_title) }) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = stringResource(Res.string.back_nav))
                    }
                },
                actions = {
                    if (viewModel != null && targetId.isNotEmpty()) {
                        val isFavorite = viewModel.isFavorite(targetId)
                        IconButton(onClick = { 
                            viewModel.togglePreferito(targetId = targetId, targetRuolo = targetRuolo) 
                        }) {
                            Icon(
                                imageVector = if (isFavorite) AppIcons.Favorite else AppIcons.FavoriteBorder,
                                contentDescription = null,
                                tint = if (isFavorite) Color.Red else Color.Gray
                            )
                        }
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .padding(16.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (farmacista != null) {
                ProfileSection(title = stringResource(Res.string.professional_data), icon = Icons.Default.Info) {
                    InfoRowItem(label = stringResource(Res.string.name_row), value = farmacista.nome)
                    InfoRowItem(label = stringResource(Res.string.surname_row), value = farmacista.cognome)
                    InfoRowItem(label = stringResource(Res.string.vat_number), value = farmacista.partitaIva)
                    farmacista.codiceFiscale?.let { InfoRowItem(label = stringResource(Res.string.tax_code_row), value = it) }
                }

                // --- AZIONI GEOGRAFICHE FARMACISTA ---
                if (farmacista.lat != null && farmacista.lng != null) {
                    val uriHandler = LocalUriHandler.current
                    Button(
                        onClick = {
                            val osmUrl = "https://www.openstreetmap.org/#map=13/${farmacista.lat}/${farmacista.lng}"
                            uriHandler.openUri(osmUrl)
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF4CAF50))
                    ) {
                        Icon(Icons.Default.LocationOn, null)
                        Spacer(Modifier.width(8.dp))
                        Text("VEDI AREA OPERATIVITÀ")
                    }
                }

                if (farmacista.competenze.isNotEmpty()) {
                    ProfileSection(title = "Competenze e Specializzazioni", icon = Icons.AutoMirrored.Filled.List) {
                        @OptIn(ExperimentalLayoutApi::class)
                        FlowRow(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            farmacista.competenze.forEach { skill ->
                                Surface(
                                    color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f),
                                    shape = RoundedCornerShape(16.dp)
                                ) {
                                    Text(
                                        text = skill,
                                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                        style = MaterialTheme.typography.labelMedium,
                                        color = MaterialTheme.colorScheme.onPrimaryContainer
                                    )
                                }
                            }
                        }
                    }
                }

                ProfileSection(title = stringResource(Res.string.contacts), icon = Icons.Default.Phone) {
                    InfoRowItem(label = stringResource(Res.string.email_row), value = farmacista.email)
                    farmacista.telefono?.let { InfoRowItem(label = stringResource(Res.string.phone_row), value = it) }
                }

                if (viewModel != null) {
                    DirectProposalSection(targetId, targetNome, targetEmail, targetRuolo, viewModel)
                }

                if (viewModel != null && viewModel.currentUserRole == UserRole.FARMACIA) {
                    TurniPharmacistSection(farmacista.email, viewModel)
                }
            } else if (farmacia != null) {
                ProfileSection(title = stringResource(Res.string.pharmacy_data), icon = AppIcons.Store) {
                    InfoRowItem(label = stringResource(Res.string.name_row), value = farmacia.nomeFarmacia)
                    InfoRowItem(label = stringResource(Res.string.vat_number), value = farmacia.partitaIva)
                }

                // --- AZIONI GEOGRAFICHE ---
                ProfileSection(title = "Localizzazione e Navigazione", icon = Icons.Default.Place) {
                    val uriHandler = LocalUriHandler.current
                    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        Button(
                            onClick = {
                                val encodedAddress = farmacia.indirizzo.replace(" ", "+")
                                val gmapsUrl = "https://www.google.com/maps/search/?api=1&query=$encodedAddress"
                                uriHandler.openUri(gmapsUrl)
                            },
                            modifier = Modifier.weight(1f).height(48.dp),
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary)
                        ) {
                            Icon(Icons.Default.Place, contentDescription = null)
                            Spacer(Modifier.width(8.dp))
                            Text("NAVIGA", fontWeight = FontWeight.Bold)
                        }
                        
                        if (farmacia.lat != null && farmacia.lng != null) {
                            Button(
                                onClick = {
                                    val osmUrl = "https://www.openstreetmap.org/?mlat=${farmacia.lat}&mlon=${farmacia.lng}#map=16/${farmacia.lat}/${farmacia.lng}"
                                    uriHandler.openUri(osmUrl)
                                },
                                modifier = Modifier.weight(1f).height(48.dp),
                                shape = RoundedCornerShape(12.dp),
                                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF4CAF50))
                            ) {
                                Icon(Icons.Default.LocationOn, contentDescription = null)
                                Spacer(Modifier.width(8.dp))
                                Text("MAPPA", fontWeight = FontWeight.Bold)
                            }
                        }
                    }
                }

                ProfileSection(title = stringResource(Res.string.location_contacts), icon = Icons.Default.LocationOn) {
                    InfoRowItem(label = stringResource(Res.string.address_row), value = farmacia.indirizzo)
                    InfoRowItem(label = stringResource(Res.string.city_row), value = "${farmacia.cap} ${farmacia.citta ?: ""} (${farmacia.provincia ?: ""})")
                    InfoRowItem(label = stringResource(Res.string.email_row), value = farmacia.email)
                    InfoRowItem(label = stringResource(Res.string.phone_row), value = farmacia.telefono)
                }

                if (viewModel != null) {
                    DirectProposalSection(targetId, targetNome, targetEmail, targetRuolo, viewModel)
                }

                if (viewModel != null) {
                    TurniFarmaciaSection(farmacia.email, viewModel)
                }
            }
        }
    }
}

@Composable
fun DirectProposalSection(targetId: String, targetNome: String, targetEmail: String, targetRuolo: String, viewModel: AuthViewModel) {
    var showDialog by remember { mutableStateOf(false) }
    var messaggio by remember { mutableStateOf("") }
    var myShifts by remember { mutableStateOf<List<AnnuncioTurno>>(emptyList()) }
    var selectedShiftIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    val role = viewModel.currentUserRole
    val uriHandler = LocalUriHandler.current
    
    val myName = if (role == UserRole.FARMACISTA) 
        "Dott./Dott.ssa ${viewModel.currentUserFarmacista?.cognome}"
    else 
        viewModel.currentUserFarmacia?.nomeFarmacia ?: "Farmacia"

    LaunchedEffect(showDialog) {
        if (showDialog) {
            myShifts = viewModel.getAnnunciUtente().filter { it.stato == AnnuncioStato.APERTO }
        }
    }

    Button(
        onClick = { 
            messaggio = if (role == UserRole.FARMACISTA) 
                "Ciao, sono $myName, vi ho trovato su Farmatch e volevo proporVi dei turni che posso svolgere."
            else 
                "Ciao, siamo la farmacia $myName, l'ho trovato/a su Farmatch e volevo proporLe dei turni che possa svolgere."
            showDialog = true 
        },
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary)
    ) {
        Icon(AppIcons.Send, contentDescription = null, modifier = Modifier.size(18.dp))
        Spacer(Modifier.width(8.dp))
        Text("FAI UNA PROPOSTA DIRETTA", fontWeight = FontWeight.Bold)
    }

    if (showDialog) {
        AlertDialog(
            onDismissRequest = { showDialog = false },
            title = { Text("Invia Proposta Diretta") },
            text = {
                Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                    Text("Invia un messaggio diretto a $targetNome. Verrà aperta la tua app di posta e inviata una notifica interna.", style = MaterialTheme.typography.bodySmall)
                    Spacer(Modifier.height(12.dp))
                    
                    if (myShifts.isNotEmpty()) {
                        Text("Allega i tuoi turni aperti:", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                        myShifts.forEach { shift ->
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Checkbox(
                                    checked = selectedShiftIds.contains(shift.id),
                                    onCheckedChange = { checked ->
                                        selectedShiftIds = if (checked) selectedShiftIds + (shift.id ?: "")
                                        else selectedShiftIds - (shift.id ?: "")
                                    }
                                )
                                Text("${shift.dataTurno.toString().substringBefore("T")} (${shift.oraInizio}-${shift.oraFine})", fontSize = 12.sp)
                            }
                        }
                        Spacer(modifier = Modifier.height(12.dp))
                    }

                    OutlinedTextField(
                        value = messaggio,
                        onValueChange = { messaggio = it },
                        modifier = Modifier.fillMaxWidth().height(150.dp),
                        label = { Text("Messaggio personalizzato") },
                        shape = RoundedCornerShape(8.dp)
                    )
                }
            },
            confirmButton = {
                Button(onClick = {
                    val selectedShiftsText = if (selectedShiftIds.isNotEmpty()) {
                        "\n\nTurni proposti:\n" + myShifts.filter { selectedShiftIds.contains(it.id) }.joinToString("\n") { 
                            "- ${it.dataTurno.toString().substringBefore("T")} (${it.oraInizio}-${it.oraFine})"
                        }
                    } else ""
                    
                    val fullMessage = "$messaggio$selectedShiftsText"

                    // 1. Inviamo la notifica interna al database
                    viewModel.sendDirectProposal(targetId, targetRuolo, fullMessage) { success ->
                        if (success) {
                            // 2. Apriamo l'app di posta locale (mailto:)
                            val subject = "Proposta di Turno da Farmatch"
                            val mailto = "mailto:$targetEmail?subject=${subject}&body=$fullMessage"
                            try {
                                uriHandler.openUri(mailto)
                            } catch (e: Exception) {
                                println("DEBUG: Errore apertura mail: ${e.message}")
                            }
                            showDialog = false
                        }
                    }
                }) { Text("INVIA") }
            },
            dismissButton = {
                TextButton(onClick = { showDialog = false }) { Text(stringResource(Res.string.cancel_button)) }
            }
        )
    }
}

@Composable
fun TurniPharmacistSection(email: String, viewModel: AuthViewModel) {
    var annunci by remember { mutableStateOf<List<AnnuncioTurno>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    
    LaunchedEffect(email) {
        annunci = viewModel.getAnnunciUtente().filter { it.creatoreId == email && it.stato == AnnuncioStato.APERTO }
        isLoading = false
    }

    ProfileSection(title = "Disponibilità del Farmacista", icon = Icons.Default.DateRange) {
        if (isLoading) {
            CircularProgressIndicator(modifier = Modifier.align(Alignment.CenterHorizontally).padding(16.dp))
        } else if (annunci.isEmpty()) {
            Text("Nessuna disponibilità pubblicata", color = Color.Gray, modifier = Modifier.padding(8.dp))
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                annunci.forEach { annuncio ->
                    ShiftRowItem(annuncio, viewModel)
                }
            }
        }
    }
}

@Composable
fun TurniFarmaciaSection(email: String, viewModel: AuthViewModel) {
    var annunci by remember { mutableStateOf<List<AnnuncioTurno>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    LaunchedEffect(email) {
        annunci = viewModel.getAnnunciFarmacia(email)
        isLoading = false
    }

    ProfileSection(title = stringResource(Res.string.available_shifts), icon = Icons.Default.DateRange) {
        if (isLoading) {
            CircularProgressIndicator(modifier = Modifier.align(Alignment.CenterHorizontally).padding(16.dp))
        } else if (annunci.isEmpty()) {
            Text(stringResource(Res.string.no_available_shifts), color = Color.Gray, modifier = Modifier.padding(8.dp))
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                annunci.forEach { annuncio ->
                    ShiftRowItem(annuncio, viewModel)
                }
            }
        }
    }
}

@Composable
fun ShiftRowItem(annuncio: AnnuncioTurno, viewModel: AuthViewModel) {
    val scope = rememberCoroutineScope()
    val activeMatch = viewModel.currentMatches.find { 
        it.annuncioId == annuncio.id && 
        it.stato != MatchStato.RIFIUTATO &&
        it.stato != MatchStato.ANNULLATO 
    }
    
    val conflictDate = viewModel.checkConflict(annuncio)

    Column {
        if (conflictDate != null && (activeMatch == null || activeMatch.stato != MatchStato.CONFERMATO)) {
            ConflictAlert(conflictDate)
        }
        
        Card(
            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f))
        ) {
            Row(
                modifier = Modifier.padding(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    val ldt = annuncio.dataTurno.toLocalDateTime(TimeZone.currentSystemDefault())
                    Text("${ldt.dayOfMonth}/${ldt.monthNumber}/${ldt.year}", fontWeight = FontWeight.Bold)
                    Text("${annuncio.oraInizio} - ${annuncio.oraFine}", style = MaterialTheme.typography.bodySmall)
                    if (annuncio.quantitaMassima > 1) {
                        val liberi = (annuncio.quantitaMassima - annuncio.postiOccupati).coerceAtLeast(0)
                        Text("Posti liberi: $liberi/${annuncio.quantitaMassima}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    }
                }
                
                Box(modifier = Modifier.width(140.dp), contentAlignment = Alignment.CenterEnd) {
                    if (activeMatch != null) {
                        MatchActionButtons(activeMatch, viewModel)
                    } else {
                        var isProcessing by remember { mutableStateOf(false) }

                        if (isProcessing) {
                            CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                        } else {
                            Button(
                                onClick = {
                                    isProcessing = true
                                    scope.launch {
                                        viewModel.respondToMatch("smart-${annuncio.id}", "ACCETTA") { success ->
                                            isProcessing = false
                                            if (success) viewModel.refreshUserData()
                                        }
                                    }
                                },
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.height(36.dp)
                            ) {
                                Text(stringResource(Res.string.accept_button), fontSize = 11.sp)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun MatchActionButtons(match: Match, viewModel: AuthViewModel) {
    var isProcessing by remember { mutableStateOf(false) }
    var showCancelConfirm by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    
    val currentUserRole = viewModel.currentUserRole
    val isProposer = match.propostoDa == (if (currentUserRole == UserRole.FARMACISTA) CreatoreTipo.FARMACISTA else CreatoreTipo.FARMACIA)

    val (label, color, actionType) = when(match.stato) {
        MatchStato.PROPOSTO -> {
            if (isProposer) Triple(stringResource(Res.string.disdici_button), Color.Red, "REJECT") 
            else Triple(stringResource(Res.string.accept_button), MaterialTheme.colorScheme.primary, "SELECT")
        }
        MatchStato.ACCETTATO -> {
            if (isProposer) Triple(stringResource(Res.string.select_button), MaterialTheme.colorScheme.primary, "SELECT") 
            else Triple(stringResource(Res.string.disdici_button), Color.Red, "REJECT")
        }
        MatchStato.CONFERMATO -> Triple(stringResource(Res.string.disdici_button), Color.Red, "REJECT")
        else -> Triple("NON DISP.", Color.Gray, "NONE")
    }

    Row(verticalAlignment = Alignment.CenterVertically) {
        if (isProcessing) {
            CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
        } else {
            Button(
                onClick = { 
                    if (actionType == "SELECT") {
                        isProcessing = true
                        scope.launch {
                            viewModel.respondToMatch(match.id ?: "", "ACCETTA") { success ->
                                isProcessing = false
                                if (success) viewModel.refreshUserData()
                            }
                        }
                    } else if (actionType == "REJECT") {
                        showCancelConfirm = true
                    }
                },
                enabled = actionType != "NONE",
                colors = ButtonDefaults.buttonColors(containerColor = color),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.height(32.dp)
            ) {
                Text(label, fontSize = 10.sp)
            }
        }
    }

    if (showCancelConfirm) {
        AlertDialog(
            onDismissRequest = { showCancelConfirm = false },
            title = { Text(stringResource(Res.string.confirm_operation)) },
            text = { Text(stringResource(Res.string.cancel_match_confirm_msg)) },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            viewModel.respondToMatch(match.id ?: "", "RIFIUTA") { success ->
                                if (success) viewModel.refreshUserData()
                            }
                        }
                        showCancelConfirm = false
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color.Red)
                ) { Text(stringResource(Res.string.yes_cancel)) }
            },
            dismissButton = {
                TextButton(onClick = { showCancelConfirm = false }) { Text(stringResource(Res.string.cancel_button)) }
            }
        )
    }
}

@Composable
fun ProfileSection(
    title: String,
    icon: ImageVector,
    content: @Composable ColumnScope.() -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp))
                Spacer(Modifier.width(8.dp))
                Text(title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
            }
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            content()
        }
    }
}

@Composable
fun InfoRowItem(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(label, style = MaterialTheme.typography.labelMedium, color = Color.Gray)
        Text(value, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Medium)
    }
}
